/**
 * LineupImportModal — Phases 1–4.
 *
 * Single mode (P1-P3): drop one screenshot → return pre-filled MatchCreate.
 * Bulk mode (P4):      drop up to 20 screenshots → background job parses
 *                      each, coach reviews the list and saves all
 *                      successes as real matches in one click.
 *
 * On single-mode Accept: calls `onImportComplete(result)` and closes.
 * On bulk save-all: matches are created directly in the DB by the backend;
 * we call `onBulkSaved({ created_count })` so the parent can refresh its
 * matches list, then close.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, X, Camera, Loader2, Check, AlertTriangle, Sparkles, Layers, Trash2, ExternalLink,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BULK_FILES = 20;
const POLL_INTERVAL_MS = 2000;

const confidenceStyle = (c) => {
  if (c === 'high') return 'bg-emerald-100 text-emerald-700';
  if (c === 'low')  return 'bg-yellow-100 text-yellow-700';
  return 'bg-gray-200 text-gray-600';
};

// Human-friendly label for the detected screenshot source. FAIConnect is
// called out explicitly since it's the mandatory app for all Irish grassroots
// leagues and is our primary target format.
const SOURCE_LABELS = {
  faiconnect: 'FAIConnect',
  scoreboard_generic: 'Scoreboard app',
  whatsapp: 'WhatsApp',
  teamsnap: 'TeamSnap',
  handwritten: 'Handwritten team-sheet',
  unknown: 'Unknown source',
};

export function LineupImportModal({ open, onOpenChange, onImportComplete, onBulkSaved }) {
  const [mode, setMode] = useState('single'); // 'single' | 'bulk'
  const navigate = useNavigate();
  // Single-mode state
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | selected | parsing | preview
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  // Bulk-mode state
  const [bulkFiles, setBulkFiles] = useState([]); // File[]
  const [bulkPhase, setBulkPhase] = useState('idle'); // idle | uploading | processing | complete | saved
  const [bulkJob, setBulkJob] = useState(null); // job doc from GET /bulk/{id}
  const [savingAll, setSavingAll] = useState(false);
  // Map of parsed-result index → created match_id, populated after save-all so
  // each row can offer a direct "Open in editor" jump link.
  const [savedByIndex, setSavedByIndex] = useState({});
  const bulkInputRef = useRef(null);
  const pollTimerRef = useRef(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setPhase('idle');
    setBulkFiles([]);
    setBulkPhase('idle');
    setBulkJob(null);
    setSavingAll(false);
    setSavedByIndex({});
    if (inputRef.current) inputRef.current.value = '';
    if (bulkInputRef.current) bulkInputRef.current.value = '';
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
  };

  // Cancel polling when modal closes
  useEffect(() => {
    if (!open && pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, [open]);

  // ─── Single mode ───────────────────────────────────────────────────────

  const handleFileSelected = (f) => {
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
      toast.error('Please choose a PNG, JPG, or WEBP image');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Image is larger than 10MB — please compress and try again');
      return;
    }
    setFile(f);
    setPhase('selected');
  };

  const upload = async () => {
    if (!file) return;
    setPhase('parsing');
    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/matches/import-screenshot`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setResult(res.data);
      setPhase('preview');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Import failed. Please try again or enter the match manually.');
      setPhase('selected');
    }
  };

  const acceptAndClose = () => {
    if (!result) return;
    onImportComplete?.(result);
    reset();
    onOpenChange(false);
  };

  // ─── Bulk mode ─────────────────────────────────────────────────────────

  const handleBulkFilesSelected = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const arr = Array.from(fileList);
    const valid = arr.filter((f) => {
      if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
        toast.error(`${f.name}: not a supported image type`);
        return false;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name}: larger than 10MB`);
        return false;
      }
      return true;
    });
    setBulkFiles((prev) => {
      const combined = [...prev, ...valid];
      if (combined.length > MAX_BULK_FILES) {
        toast.error(`Bulk import limit is ${MAX_BULK_FILES} screenshots per job.`);
        return combined.slice(0, MAX_BULK_FILES);
      }
      return combined;
    });
  };

  const removeBulkFile = (idx) => {
    setBulkFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const pollBulkJob = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/matches/import-screenshot/bulk/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBulkJob(res.data);
      if (res.data.status === 'complete' || res.data.status === 'failed') {
        setBulkPhase('complete');
        if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
      } else {
        pollTimerRef.current = setTimeout(() => pollBulkJob(jobId), POLL_INTERVAL_MS);
      }
    } catch (err) {
      toast.error('Lost connection to the import job. Please refresh and check the matches list.');
      setBulkPhase('complete');
    }
  };

  const startBulkImport = async () => {
    if (bulkFiles.length === 0) return;
    setBulkPhase('uploading');
    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      bulkFiles.forEach((f) => form.append('files', f));
      const res = await axios.post(`${API}/matches/import-screenshot/bulk`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const jobId = res.data.job_id;
      setBulkJob({ ...res.data, results: [], processed: 0 });
      setBulkPhase('processing');
      pollBulkJob(jobId);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to start bulk import.');
      setBulkPhase('idle');
    }
  };

  const saveAllBulkMatches = async () => {
    if (!bulkJob?.id) return;
    setSavingAll(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/matches/import-screenshot/bulk/${bulkJob.id}/save-all`,
        {}, // no filter — save every success
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
      );
      const created = res.data.created || [];
      const n = res.data.created_count || 0;
      const map = {};
      created.forEach((c) => { map[c.index] = c.match_id; });
      setSavedByIndex(map);
      setBulkPhase('saved');  // stay open with jump-links instead of closing
      toast.success(`Created ${n} match${n === 1 ? '' : 'es'} — jump into any to review players.`);
      onBulkSaved?.({ created_count: n, job_id: bulkJob.id, created });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to save matches.');
    } finally {
      setSavingAll(false);
    }
  };

  const openMatch = (matchId) => {
    // Persist any interim state before navigating so the parent list can
    // refresh. Modal is closed cleanly to avoid a stale overlay.
    reset();
    onOpenChange(false);
    navigate(`/matches/${matchId}/edit`);
  };

  // ─── Rendering ─────────────────────────────────────────────────────────

  const pf = result?.pre_filled_match;
  const starterCount = pf?.starters?.length || 0;
  const subCount = pf?.subs_bench?.length || 0;
  const eventCount = pf?.events?.length || 0;
  const unmatchedCount = [
    ...(pf?.starters || []),
    ...(pf?.subs_bench || []),
  ].filter((p) => !p.player_id).length;

  const successfulCount = bulkJob?.results?.filter((r) => r.status === 'success').length || 0;
  const failedCount = bulkJob?.results?.filter((r) => r.status === 'failed').length || 0;
  const progressPct = bulkJob?.total ? Math.round((bulkJob.processed / bulkJob.total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lineup-import-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-[#007a33]" />
            Import lineup from screenshot
          </DialogTitle>
          <DialogDescription>
            Drop a screenshot from your league app, WhatsApp chat, or ref card. We&apos;ll read the lineup, goals, cards and subs automatically.
          </DialogDescription>
        </DialogHeader>

        {/* Mode toggle — hidden once single-mode has entered parsing/preview or
            bulk-mode has started uploading (so the coach can't accidentally
            switch mid-flow and lose state). */}
        {phase !== 'parsing' && phase !== 'preview' && bulkPhase === 'idle' && (
          <div className="mt-3 flex items-center gap-1 p-1 rounded-lg bg-gray-100" data-testid="lineup-import-mode-tabs">
            <button
              type="button"
              onClick={() => setMode('single')}
              className={`flex-1 text-xs font-semibold px-3 py-1.5 rounded transition-colors ${mode === 'single' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid="lineup-import-mode-single"
            >
              Single screenshot
            </button>
            <button
              type="button"
              onClick={() => setMode('bulk')}
              className={`flex-1 text-xs font-semibold px-3 py-1.5 rounded transition-colors flex items-center justify-center gap-1 ${mode === 'bulk' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid="lineup-import-mode-bulk"
            >
              <Layers className="w-3 h-3" />
              Bulk (up to {MAX_BULK_FILES})
            </button>
          </div>
        )}

        {/* ═════════ SINGLE MODE ═════════ */}
        {mode === 'single' && (phase === 'idle' || phase === 'selected') && (
          <>
            <div
              className={`mt-4 border-2 border-dashed rounded-xl p-8 text-center transition-colors ${file ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 hover:border-[#007a33] bg-gray-50'}`}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); handleFileSelected(e.dataTransfer.files?.[0]); }}
              data-testid="lineup-import-dropzone"
            >
              <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => handleFileSelected(e.target.files?.[0])}
                data-testid="lineup-import-file-input"
              />
              {file ? (
                <div className="space-y-2">
                  <p className="font-semibold text-emerald-700 truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB &middot; {file.type}</p>
                  <button type="button" onClick={reset} className="text-xs text-gray-500 hover:text-gray-700 underline" data-testid="lineup-import-clear-btn">
                    Choose a different file
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="w-10 h-10 mx-auto text-gray-400" />
                  <p className="text-sm font-semibold text-gray-700">Drop your screenshot here</p>
                  <p className="text-xs text-gray-500">or</p>
                  <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} data-testid="lineup-import-pick-btn">
                    Choose file
                  </Button>
                  <p className="text-[11px] text-gray-400 mt-2">PNG, JPG or WEBP &middot; max 10MB</p>
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="lineup-import-cancel-btn">
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button type="button" onClick={upload} disabled={!file} className="bg-[#007a33] hover:bg-[#005c26] text-white" data-testid="lineup-import-submit-btn">
                <Sparkles className="w-4 h-4 mr-1.5" />
                Import lineup
              </Button>
            </div>
          </>
        )}

        {mode === 'single' && phase === 'parsing' && (
          <div className="mt-4 py-12 text-center space-y-3" data-testid="lineup-import-parsing">
            <Loader2 className="w-10 h-10 mx-auto text-[#007a33] animate-spin" />
            <p className="text-sm font-semibold text-gray-700">Reading your screenshot…</p>
            <p className="text-xs text-gray-500">Usually takes 3–6 seconds</p>
          </div>
        )}

        {mode === 'single' && phase === 'preview' && result && pf && (
          <div className="mt-4 space-y-4" data-testid="lineup-import-preview">
            <div className="flex items-center justify-between gap-2 p-3 rounded-lg bg-gray-50 border border-gray-200">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Detected source</p>
                <p className="text-sm font-semibold text-gray-900 truncate" data-testid="lineup-import-detected-source">
                  {SOURCE_LABELS[result.detected_source] || SOURCE_LABELS.unknown}
                </p>
              </div>
              <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${confidenceStyle(result.overall_confidence)}`}>
                {result.overall_confidence}
              </span>
            </div>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm border-t border-b border-gray-200 py-3">
              <dt className="text-gray-500">Opponent</dt>
              <dd className="text-gray-900 font-semibold text-right">{pf.opponent || <span className="text-gray-400">—</span>}</dd>
              <dt className="text-gray-500">Date · Kick-off</dt>
              <dd className="text-gray-900 font-medium text-right">
                {pf.date || '—'}{pf.kick_off_time ? ` · ${pf.kick_off_time}` : ''}
              </dd>
              <dt className="text-gray-500">Venue</dt>
              <dd className="text-gray-900 font-medium text-right capitalize">{pf.venue || <span className="text-gray-400">—</span>}</dd>
              <dt className="text-gray-500">Competition</dt>
              <dd className="text-gray-900 font-medium text-right">{pf.competition || <span className="text-gray-400">—</span>}</dd>
              <dt className="text-gray-500">Final score</dt>
              <dd className="text-gray-900 font-bold text-right">
                {pf.our_score != null && pf.opponent_score != null
                  ? `${pf.our_score} – ${pf.opponent_score}`
                  : <span className="text-gray-400 font-normal">—</span>}
              </dd>
            </dl>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded-lg bg-gray-50">
                <p className="text-lg font-black text-gray-900" data-testid="lineup-preview-starters-count">{starterCount}</p>
                <p className="text-[10px] uppercase tracking-widest text-gray-500">Starters</p>
              </div>
              <div className="p-2 rounded-lg bg-gray-50">
                <p className="text-lg font-black text-gray-900" data-testid="lineup-preview-subs-count">{subCount}</p>
                <p className="text-[10px] uppercase tracking-widest text-gray-500">Subs</p>
              </div>
              <div className="p-2 rounded-lg bg-gray-50">
                <p className="text-lg font-black text-gray-900" data-testid="lineup-preview-events-count">{eventCount}</p>
                <p className="text-[10px] uppercase tracking-widest text-gray-500">Events</p>
              </div>
            </div>
            {(result.warnings?.length > 0 || result.parse_notes) && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 space-y-1.5">
                {result.parse_notes && <p className="text-xs text-amber-900 italic">{result.parse_notes}</p>}
                {result.warnings?.map((w, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-xs text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{w}</span>
                  </div>
                ))}
                {unmatchedCount > 0 && (
                  <p className="text-[11px] text-amber-700 pt-1">
                    You&apos;ll be able to correct unmatched players after applying.
                  </p>
                )}
              </div>
            )}
            {starterCount > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5">Starters detected</p>
                <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
                  {pf.starters.map((p, i) => (
                    <li key={i} className="flex items-center justify-between text-xs">
                      <span className={p.player_id ? 'text-gray-800' : 'text-gray-400 italic'}>
                        {p.number ? `#${p.number} ` : ''}{p.matched_name || p.detected_name}
                      </span>
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${confidenceStyle(p.confidence)}`}>
                        {p.player_id ? p.confidence : 'unmatched'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={reset} data-testid="lineup-import-retry-btn">
                Try another
              </Button>
              <Button type="button" onClick={acceptAndClose} className="bg-[#007a33] hover:bg-[#005c26] text-white" data-testid="lineup-import-accept-btn">
                <Check className="w-4 h-4 mr-1.5" />
                Use these details
              </Button>
            </div>
          </div>
        )}

        {/* ═════════ BULK MODE ═════════ */}
        {mode === 'bulk' && bulkPhase === 'idle' && (
          <>
            <div
              className={`mt-4 border-2 border-dashed rounded-xl p-6 transition-colors ${bulkFiles.length > 0 ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-300 hover:border-[#007a33] bg-gray-50'}`}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); handleBulkFilesSelected(e.dataTransfer.files); }}
              data-testid="lineup-import-bulk-dropzone"
            >
              <input
                ref={bulkInputRef}
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => handleBulkFilesSelected(e.target.files)}
                data-testid="lineup-import-bulk-file-input"
              />
              {bulkFiles.length === 0 ? (
                <div className="space-y-3 text-center">
                  <Layers className="w-10 h-10 mx-auto text-gray-400" />
                  <p className="text-sm font-semibold text-gray-700">Drop up to {MAX_BULK_FILES} screenshots</p>
                  <p className="text-xs text-gray-500">A season&apos;s worth in one go — we&apos;ll parse each in the background.</p>
                  <Button type="button" variant="outline" onClick={() => bulkInputRef.current?.click()} data-testid="lineup-import-bulk-pick-btn">
                    Choose files
                  </Button>
                  <p className="text-[11px] text-gray-400">PNG, JPG or WEBP &middot; max 10MB each</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-700">{bulkFiles.length} of {MAX_BULK_FILES} selected</p>
                    <Button type="button" variant="ghost" size="sm" onClick={() => bulkInputRef.current?.click()} data-testid="lineup-import-bulk-add-more">
                      + Add more
                    </Button>
                  </div>
                  <ul className="space-y-1 max-h-48 overflow-y-auto pr-1">
                    {bulkFiles.map((f, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 text-xs px-2 py-1 rounded bg-white border border-gray-200" data-testid={`bulk-file-${i}`}>
                        <span className="truncate text-gray-800">{f.name}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-gray-400">{(f.size / 1024).toFixed(0)} KB</span>
                          <button type="button" onClick={() => removeBulkFile(i)} className="text-gray-400 hover:text-red-500" data-testid={`bulk-file-remove-${i}`} aria-label={`Remove ${f.name}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="lineup-import-bulk-cancel-btn">
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button
                type="button"
                onClick={startBulkImport}
                disabled={bulkFiles.length === 0}
                className="bg-[#007a33] hover:bg-[#005c26] text-white"
                data-testid="lineup-import-bulk-start-btn"
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                Import {bulkFiles.length || ''} screenshot{bulkFiles.length === 1 ? '' : 's'}
              </Button>
            </div>
          </>
        )}

        {mode === 'bulk' && (bulkPhase === 'uploading' || bulkPhase === 'processing') && (
          <div className="mt-4 py-8 space-y-4" data-testid="lineup-import-bulk-processing">
            <div className="text-center space-y-2">
              <Loader2 className="w-10 h-10 mx-auto text-[#007a33] animate-spin" />
              <p className="text-sm font-semibold text-gray-700">
                {bulkPhase === 'uploading' ? 'Uploading screenshots…' : 'Reading your screenshots…'}
              </p>
              <p className="text-xs text-gray-500">
                {bulkJob?.processed || 0} of {bulkJob?.total || bulkFiles.length} done · ~5-8s each
              </p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden" data-testid="bulk-progress-bar">
              <div
                className="bg-[#007a33] h-full transition-all duration-500 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            {bulkJob?.results?.length > 0 && (
              <ul className="space-y-1 max-h-48 overflow-y-auto pr-1 text-xs">
                {bulkJob.results.map((r) => (
                  <li key={r.index} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-gray-50 border border-gray-200">
                    <span className="truncate text-gray-700">
                      {r.status === 'success' ? '✓' : '✕'} {r.filename}
                    </span>
                    <span className="text-gray-500 shrink-0">
                      {r.status === 'success' ? `${r.opponent || 'Unknown'} · ${r.date || '—'}` : 'failed'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {mode === 'bulk' && bulkPhase === 'complete' && bulkJob && (
          <div className="mt-4 space-y-4" data-testid="lineup-import-bulk-complete">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-200">
                <p className="text-2xl font-black text-emerald-700" data-testid="bulk-success-count">{successfulCount}</p>
                <p className="text-[10px] uppercase tracking-widest text-emerald-800">Parsed</p>
              </div>
              <div className="p-2 rounded-lg bg-red-50 border border-red-200">
                <p className="text-2xl font-black text-red-700" data-testid="bulk-failed-count">{failedCount}</p>
                <p className="text-[10px] uppercase tracking-widest text-red-800">Failed</p>
              </div>
              <div className="p-2 rounded-lg bg-gray-50 border border-gray-200">
                <p className="text-2xl font-black text-gray-900">{bulkJob.total}</p>
                <p className="text-[10px] uppercase tracking-widest text-gray-500">Total</p>
              </div>
            </div>
            <ul className="space-y-1 max-h-64 overflow-y-auto pr-1 text-xs" data-testid="bulk-results-list">
              {(bulkJob.results || []).map((r) => (
                <li
                  key={r.index}
                  className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded border ${r.status === 'success' ? 'bg-white border-gray-200' : 'bg-red-50 border-red-200'}`}
                  data-testid={`bulk-result-${r.index}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-gray-800">
                      {r.status === 'success' ? '✓' : '✕'} {r.opponent || r.filename}
                    </p>
                    {r.status === 'success' ? (
                      <p className="text-[10px] text-gray-500 truncate">
                        {r.date || 'no date'} · {r.starters_count}/{r.subs_count}/{r.events_count} starters/subs/events
                        {r.unmatched_count > 0 ? ` · ${r.unmatched_count} to review` : ''}
                      </p>
                    ) : (
                      <p className="text-[10px] text-red-600 truncate" title={r.error}>{r.error || 'Parse error'}</p>
                    )}
                  </div>
                  {r.status === 'success' && (
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${confidenceStyle(r.overall_confidence)}`}>
                      {r.overall_confidence}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {successfulCount > 0 && (
              <p className="text-[11px] text-gray-500 text-center">
                Matches will be created as drafts — you can review unmatched players and captain/formation later in the match editor.
              </p>
            )}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={reset} data-testid="lineup-import-bulk-retry-btn">
                Start over
              </Button>
              <Button
                type="button"
                onClick={saveAllBulkMatches}
                disabled={successfulCount === 0 || savingAll}
                className="bg-[#007a33] hover:bg-[#005c26] text-white"
                data-testid="lineup-import-bulk-save-all-btn"
              >
                {savingAll ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving…</>
                ) : (
                  <><Check className="w-4 h-4 mr-1.5" />Save all {successfulCount} match{successfulCount === 1 ? '' : 'es'}</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ═════════ BULK — SAVED (post save-all) ═════════
            Same results list, but successful rows now carry an "Open in
            editor" button linked to /matches/{id}/edit so the coach can
            jump straight into any created match to fix unmatched players,
            set captain, formation, etc. Onboarding-turbo experience. */}
        {mode === 'bulk' && bulkPhase === 'saved' && bulkJob && (
          <div className="mt-4 space-y-4" data-testid="lineup-import-bulk-saved">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
              <Check className="w-6 h-6 mx-auto text-emerald-600" />
              <p className="text-sm font-bold text-emerald-800 mt-1">
                {Object.keys(savedByIndex).length} match{Object.keys(savedByIndex).length === 1 ? '' : 'es'} saved as drafts
              </p>
              <p className="text-[11px] text-emerald-700 mt-0.5">Tap any one to review unmatched players, captain and formation.</p>
            </div>
            <ul className="space-y-1 max-h-72 overflow-y-auto pr-1 text-xs" data-testid="bulk-saved-list">
              {(bulkJob.results || []).filter((r) => r.status === 'success').map((r) => {
                const matchId = savedByIndex[r.index];
                return (
                  <li
                    key={r.index}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 rounded border bg-white border-gray-200"
                    data-testid={`bulk-saved-${r.index}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-gray-800">✓ {r.opponent || r.filename}</p>
                      <p className="text-[10px] text-gray-500 truncate">
                        {r.date || 'no date'} · {r.starters_count}/{r.subs_count}/{r.events_count} starters/subs/events
                        {r.unmatched_count > 0 ? ` · ${r.unmatched_count} to review` : ''}
                      </p>
                    </div>
                    {matchId ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => openMatch(matchId)}
                        className="shrink-0 h-7 px-2 text-[11px]"
                        data-testid={`bulk-open-match-${r.index}`}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Open
                      </Button>
                    ) : (
                      <span className="text-[10px] text-gray-400 shrink-0">not saved</span>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
              <Button
                type="button"
                onClick={() => { reset(); onOpenChange(false); }}
                className="bg-[#007a33] hover:bg-[#005c26] text-white"
                data-testid="lineup-import-bulk-done-btn"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default LineupImportModal;
